import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select
} from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useFacebookSdk from "../../hooks/useFacebookSdk";

// O popup do Embedded Signup publica de facebook.com. Qualquer outra origem
// é descartada sem sequer olhar o conteúdo.
const ALLOWED_ORIGINS = [
  "https://www.facebook.com",
  "https://facebook.com",
  "https://web.facebook.com"
];

// Rede de segurança: se o usuário abandonar o popup sem gerar CANCEL/ERROR
// (fechar pela barra do sistema, perder conexão), o botão ficaria travado em
// "Conectando..." para sempre. Isso NÃO sincroniza nada — só limpa estado.
const ABANDON_TIMEOUT_MS = 90000;

// Coexistência: cliente mantém o WhatsApp Business no celular E ganha a API
// no mesmo número. É o featureType documentado pela Meta para esse sub-fluxo.
export const FEATURE_TYPE_COEXISTENCE = "whatsapp_business_app_onboarding";

// Janelas oferecidas no seletor. O backend aceita qualquer "<n>d"/"<n>m"
// (ver historyPolicy.ts); aqui ficam só as opções que fazem sentido clicar.
export const OPCOES_HISTORICO = ["full", "6m", "30d", "15d", "off"];

const POLITICA_PADRAO = "full";

// Eventos de conclusão aceitos. O de coexistência devolve APENAS waba_id —
// o phone_number_id é resolvido no backend consultando a WABA.
const FINISH_EVENTS = [
  "FINISH",
  "FINISH_ONLY_WABA",
  "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING"
];

/**
 * Botão do Meta Embedded Signup.
 *
 * Fluxo: FB.login devolve um `code` no callback, enquanto o popup publica os
 * ids do número via postMessage (evento FINISH). As duas coisas chegam em
 * ORDEM NÃO GARANTIDA, então guardamos cada pedaço em um ref e só chamamos o
 * backend quando ambos existem — sem setTimeout, sem corrida.
 *
 * O `code` é de uso único, nunca é logado e só trafega para o nosso backend.
 *
 * `featureType` vazio = fluxo padrão (número novo/migrado só no Cloud API).
 * Com FEATURE_TYPE_COEXISTENCE, a Meta troca a tela de escolha de WABA pela
 * de conectar a conta que já existe no aplicativo.
 *
 * Podem existir DUAS instâncias na tela (uma por opção do menu) e as duas
 * escutam o mesmo `message`. Só submete quem tem `code`, e todo clique começa
 * com resetPending(), então a instância que não abriu o popup não guarda
 * estado de uma rodada anterior.
 */
const MetaEmbeddedSignupButton = ({
  disabled,
  onSuccess,
  render,
  featureType = ""
}) => {
  const { config, isAvailable, ensureSdk } = useFacebookSdk();
  const [loading, setLoading] = useState(false);

  // Seletor de histórico: só existe no modo coexistência.
  const [perguntaAberta, setPerguntaAberta] = useState(false);
  const [politica, setPolitica] = useState(POLITICA_PADRAO);
  const politicaRef = useRef(POLITICA_PADRAO);

  const codeRef = useRef(null);
  const signupRef = useRef(null);
  const mountedRef = useRef(true);
  const abandonTimerRef = useRef(null);

  const clearAbandonTimer = useCallback(() => {
    if (abandonTimerRef.current) {
      clearTimeout(abandonTimerRef.current);
      abandonTimerRef.current = null;
    }
  }, []);

  // Todo caminho terminal passa por aqui, então é o lugar certo para desarmar
  // o timer de abandono.
  const resetPending = useCallback(() => {
    codeRef.current = null;
    signupRef.current = null;
    clearAbandonTimer();
  }, [clearAbandonTimer]);

  const safeSetLoading = useCallback(value => {
    if (mountedRef.current) setLoading(value);
  }, []);

  const isCoexistence = featureType === FEATURE_TYPE_COEXISTENCE;

  const submit = useCallback(
    async (code, phoneNumberId, wabaId) => {
      try {
        safeSetLoading(true);

        const { data } = await api.post("/plugins/meta-signup/exchange-code", {
          code,
          phone_number_id: phoneNumberId,
          waba_id: wabaId,
          mode: isCoexistence ? "coexistence" : "cloud_api",
          // Só no modo coexistência: no clássico não existe histórico
          // para importar, e mandar o campo confundiria o log.
          ...(isCoexistence ? { importHistory: politicaRef.current } : {})
        });

        toast.success(i18n.t("connections.metaSignup.success"));

        if (onSuccess) onSuccess(data);
      } catch (err) {
        toastError(err);
      } finally {
        safeSetLoading(false);
      }
    },
    [isCoexistence, onSuccess, safeSetLoading]
  );

  /**
   * Dispara assim que o code E os ids do número estiverem disponíveis.
   * Chamada pelos dois lados; quem chegar por último efetiva o envio.
   */
  const trySubmit = useCallback(() => {
    if (!codeRef.current || !signupRef.current) return;

    const code = codeRef.current;
    const { phoneNumberId, wabaId } = signupRef.current;

    resetPending();

    submit(code, phoneNumberId, wabaId);
  }, [resetPending, submit]);

  useEffect(() => {
    mountedRef.current = true;

    const handleMessage = event => {
      if (!ALLOWED_ORIGINS.includes(event.origin)) return;

      let payload;

      try {
        payload =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch (err) {
        // Mensagem que não é JSON não é do Embedded Signup.
        return;
      }

      if (payload?.type !== "WA_EMBEDDED_SIGNUP") return;

      if (FINISH_EVENTS.includes(payload.event)) {
        signupRef.current = {
          phoneNumberId: payload.data?.phone_number_id,
          wabaId: payload.data?.waba_id
        };
        trySubmit();
        return;
      }

      if (payload.event === "CANCEL") {
        resetPending();
        safeSetLoading(false);
        toast.warn(i18n.t("connections.metaSignup.cancelled"));
        return;
      }

      if (payload.event === "ERROR") {
        resetPending();
        safeSetLoading(false);
        toast.error(
          payload.data?.error_message || i18n.t("connections.metaSignup.error")
        );
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      mountedRef.current = false;
      window.removeEventListener("message", handleMessage);
      clearAbandonTimer();
    };
  }, [clearAbandonTimer, resetPending, safeSetLoading, trySubmit]);

  const abrirSignup = useCallback(async () => {
    if (loading) return;

    if (!isAvailable) {
      toast.error(i18n.t("connections.metaSignup.notConfigured"));
      return;
    }

    resetPending();

    try {
      safeSetLoading(true);

      const FB = await ensureSdk();

      FB.login(
        response => {
          const code = response?.authResponse?.code;

          if (!code) {
            // Usuário fechou o popup ou negou. O evento CANCEL do postMessage
            // pode já ter tratado; aqui apenas destravamos o botão.
            resetPending();
            safeSetLoading(false);
            return;
          }

          codeRef.current = code;
          trySubmit();
        },
        {
          config_id: config.configId,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType,
            sessionInfoVersion: "3"
          }
        }
      );

      // Armado só depois que o popup abriu. Se nem o code nem o FINISH
      // chegarem, destrava o botão — não substitui a sincronização, que
      // continua sendo por refs.
      abandonTimerRef.current = setTimeout(() => {
        abandonTimerRef.current = null;
        if (!mountedRef.current) return;
        codeRef.current = null;
        signupRef.current = null;
        setLoading(false);
        toast.error(i18n.t("connections.metaSignup.error"));
      }, ABANDON_TIMEOUT_MS);
    } catch (err) {
      resetPending();
      safeSetLoading(false);
      toast.error(err?.message || i18n.t("connections.metaSignup.sdkError"));
    }
  }, [
    config,
    ensureSdk,
    featureType,
    isAvailable,
    loading,
    resetPending,
    safeSetLoading,
    trySubmit
  ]);

  /**
   * No modo coexistência a política de histórico precisa estar decidida
   * ANTES de abrir o popup: os webhooks "history" começam a chegar logo
   * depois do onboarding, e a conexão já tem que estar marcada quando o
   * primeiro lote cair. Por isso o seletor vem antes, não depois.
   */
  const handleClick = useCallback(() => {
    if (loading) return;

    if (!isCoexistence) {
      abrirSignup();
      return;
    }

    setPerguntaAberta(true);
  }, [abrirSignup, isCoexistence, loading]);

  const confirmarPolitica = useCallback(() => {
    politicaRef.current = politica;
    setPerguntaAberta(false);
    abrirSignup();
  }, [abrirSignup, politica]);

  return (
    <>
      {render({
        onClick: handleClick,
        loading,
        disabled: disabled || !isAvailable
      })}

      <Dialog
        open={perguntaAberta}
        onClose={() => setPerguntaAberta(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {i18n.t("connections.metaSignup.historyTitle")}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth variant="outlined" size="small">
            <Select
              value={politica}
              onChange={e => setPolitica(e.target.value)}
            >
              {OPCOES_HISTORICO.map(opcao => (
                <MenuItem key={opcao} value={opcao}>
                  {i18n.t(`connections.metaSignup.history.${opcao}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPerguntaAberta(false)}>
            {i18n.t("connections.metaSignup.historyCancel")}
          </Button>
          <Button onClick={confirmarPolitica} color="primary">
            {i18n.t("connections.metaSignup.historyConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MetaEmbeddedSignupButton;
